import React, { useState, useEffect, FormEvent } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clipboard, Trash2, RefreshCw } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";



// Define GraphQL operations
const LIST_TOKENS = gql`
  query ListTokens {
    listTokens
    {
      id
      userID
      token
      createdAt
      isRevoked
      expiresAt
      state
      userName
    }
  }
`;

const ISSUE_TOKEN = gql`
  mutation IssueToken($username: String!) {
    issueToken(username: $username)
  }
`;

const REVOKE_TOKEN = gql`
  mutation RevokeToken($id: Int!) {
    revokeToken(id: $id)
  }
`;

const LIST_USERS = gql`
  query ListUsers($filters: FilterOptions) {
    listUsers(filters: $filters) {
      entries {
        id
        username
        name
        disabled
      }
      totalCount
    }
  }
`;

// Updated Token interface to match new API fields
interface Token {
  id: number;
  userID: string;
  token: string;
  createdAt: string;
  isRevoked: boolean;
  expiresAt: string | null;
  state: string;
  userName: string;
}

interface User {
  id: string;
  username: string;
  name: string;
  disabled: boolean;
}

interface UserResponse {
  listUsers: {
    entries: User[];
    totalCount: number;
  };
}

export const TokenManager = () => {
  const [username, setUsername] = useState<string>('');
  const [newToken, setNewToken] = useState<string | null>(null);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tokenToRevoke, setTokenToRevoke] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [filterOption, setFilterOption] = useState<'all' | 'noexpire'>('noexpire');
  
  // Fetch tokens
  const { loading: loadingTokens, refetch: refetchTokens } = useQuery(LIST_TOKENS, {
    onCompleted: (data) => {
      try {
        setTokens(data.listTokens);
      } catch (error) {
        console.error('Error parsing tokens:', error);
        toast.error('Failed to parse token data');
      }
    },
    onError: (error) => {
      toast.error(`Error fetching tokens: ${error.message}`);
    }
  });

  // Fetch users
  const { loading: loadingUsers } = useQuery(LIST_USERS, {
    variables: {
      filters: {
        showDisabled: false,
        limit: 1000,
        offset: 0
      }
    },
    onCompleted: (data: UserResponse) => {
      try {
        setUsers(data.listUsers.entries);
      } catch (error) {
        console.error('Error parsing users:', error);
        toast.error('Failed to parse user data');
      }
    },
    onError: (error) => {
      toast.error(`Error fetching users: ${error.message}`);
    }
  });

  // Issue token mutation
  const [issueToken, { loading: issuingToken }] = useMutation(ISSUE_TOKEN, {
    onCompleted: (data) => {
      const tokenValue = data.issueToken;
      setNewToken(tokenValue);
      refetchTokens();
      toast.success('Token issued successfully!');
    },
    onError: (error) => {
      toast.error(`Error issuing token: ${error.message}`);
    }
  });

  // Revoke token mutation
  const [revokeToken, { loading: revokingToken }] = useMutation(REVOKE_TOKEN, {
    onCompleted: () => {
      refetchTokens();
      toast.success('Token revoked successfully!');
    },
    onError: (error) => {
      toast.error(`Error revoking token: ${error.message}`);
    }
  });

  const handleIssueSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username.trim()) {
      toast.warn('Please select a username');
      return;
    }
    
    issueToken({ variables: { username: username.trim() } });
  };

  const handleRevokeClick = (id: number) => {
    setTokenToRevoke(id);
    setIsDialogOpen(true);
  };

  const confirmRevoke = () => {
    if (tokenToRevoke !== null) {
      revokeToken({ variables: { id: tokenToRevoke } });
      setIsDialogOpen(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => toast.info('Copied to clipboard!'))
      .catch(() => toast.error('Failed to copy'));
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  // Function to determine token status display
  const getStatusDisplay = (token: Token) => {
    if (token.isRevoked) return { text: 'Revoked', classes: 'bg-red-100 text-red-800' };
    if (token.state === 'active') return { text: 'Active', classes: 'bg-green-100 text-green-800' };
    if (token.state === 'expired') return { text: 'Expired', classes: 'bg-yellow-100 text-yellow-800' };
    return { text: token.state, classes: 'bg-gray-100 text-gray-800' };
  };

  const getFilteredTokens = () => {
    return filterOption === 'all' ? tokens : tokens.filter(token => token.state === 'noexpire');
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-3xl font-bold mb-6">Token Manager</h1>
      
      <Tabs defaultValue="list">
        <TabsList className="mb-4">
          <TabsTrigger value="list">View Tokens</TabsTrigger>
          <TabsTrigger value="issue">Issue Token</TabsTrigger>
        </TabsList>
        
        <TabsContent value="list">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Active Tokens</CardTitle>
                  <CardDescription>Manage user access tokens</CardDescription>
                </div>
                <select
                  className="border rounded p-2"
                  value={filterOption}
                  onChange={(e) => setFilterOption(e.target.value as 'all' | 'noexpire')}
                >                  
                  <option value="noexpire">No Expiry Only</option>
                  <option value="all">All Tokens</option>
                </select>
              </div>
            </CardHeader>
            <CardContent>
              {loadingTokens ? (
                <div className="flex justify-center py-8">Loading tokens...</div>
              ) : getFilteredTokens().length === 0 ? (
                <div className="text-center py-8 text-gray-500">No tokens found</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User Name</TableHead>
                        <TableHead>Created At</TableHead>
                        <TableHead>Expires At</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredTokens().map((token) => {
                        const status = getStatusDisplay(token);
                        return (
                          <TableRow key={token.id} className="hover:bg-gray-100 text-xs text-gray-900">
                            <TableCell>{token.userName}</TableCell>
                            <TableCell>{formatDate(token.createdAt)}</TableCell>
                            <TableCell>{formatDate(token.expiresAt)}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.classes}`}>
                                {status.text}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {!token.isRevoked && (
                                  <Button 
                                    variant="destructive" 
                                    size="sm" 
                                    onClick={() => handleRevokeClick(token.id)}
                                    disabled={revokingToken}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Revoke
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
              <div className="mt-4 flex justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => refetchTokens()}
                  disabled={loadingTokens}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="issue">
          <Card>
            <CardHeader>
              <CardTitle>Issue Token</CardTitle>
              <CardDescription>Generate a token for a user</CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleIssueSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username">Select User</Label>
                  {loadingUsers ? (
                    <div className="text-xs text-gray-500">Loading users...</div>
                  ) : (
                    <Select 
                      value={username} 
                      onValueChange={setUsername}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a user" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.username}>
                            {user.name} ({user.username})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                <Button type="submit" className="w-full" disabled={issuingToken || !username.trim()}>
                  {issuingToken ? 'Issuing...' : 'Issue Token'}
                </Button>
              </form>

              {newToken && (
                <div className="mt-6 p-4 border rounded-md bg-gray-50">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-medium">Token:</h3>
                  </div>
                  
                  <div className="relative">
                    <div className="p-3 bg-gray-100 rounded border text-xs font-mono overflow-x-auto break-all">
                      {newToken}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(newToken)}
                    >
                      <Clipboard className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Confirmation Dialog */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Token?</AlertDialogTitle>
            <AlertDialogDescription>
              This action will revoke the selected token. Any systems using this token will no longer have access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRevoke} className="bg-red-600 hover:bg-red-700">
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};